import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocalizedText } from '../../lib/localizedText'
import { exportMeetingTypes } from './api/meetingTypeApi'
import Button from '../../components/ui/Button'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import CardPaginator from '../../components/ui/CardPaginator'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import FilterBar from '../../components/ui/FilterBar'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import { useToast } from '../../components/ui/ToastProvider'
import Badge from '../../components/ui/Badge'
import { InputSwitch } from 'primereact/inputswitch'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import { useProxyMode } from '../proxy/useProxyMode'
import { getApiErrorCode, isOptimisticLock } from '../../api/apiError'
import { meetingTypeColumns } from './components/MeetingTypeColumns'
import MeetingTypeFormDialog from './components/MeetingTypeFormDialog'
import { useMeetingTypes } from './hooks/useMeetingTypes'
import type { MeetingType, MeetingTypeUpsert } from './types'
// Layout sınıfları (admin-page/admin-form/admin-row-actions/admin-switch) paylaşılan.
import '../admin/admin.css'

/** Kart listesinin sayfa boyutu (anayasa: tüm listelerde 10 — DataTable de aynı varsayılanı kullanır). */
const CARD_PAGE_SIZE = 10

/** Arama gecikmesi (Page Standards) — her tuşta sunucuya gidilmez. */
const SEARCH_DEBOUNCE_MS = 300

/**
 * <b>Admin → Parametreler → Toplantı Türü</b> yönetim ekranı (22.09.2026). Liste (paylaşılan
 * `ui/DataTable`) + ekle/düzenle (`FormDialog`) + Aktif/Pasif anahtarı + soft-delete (onaylı).
 * Başlık = menü adı (`nav.admin.meetingTypes`) — anayasa kuralı.
 *
 * <p><b>Sıralama (↑/↓) yok</b>: `REF_MEETING_TYPE`'ta `SORT_INDEX` kolonu bulunmadığından liste ada
 * göre gelir; sıralanabilir-lookup kart bileşeni (`ReorderableLookupCards`) de bu yüzden kullanılmaz —
 * onun tek varlık sebebi taşıma oklarıdır.
 *
 * <p><b>Dar ekranda kart listesi ÇİZİLİR</b> (anayasa "Dar ekran kart kalıbı"): kural sıralamadan
 * bağımsızdır — dört kolonlu bir tabloyu telefonda yatay kaydırtmak yasaktır. Reorder'ı olmayan
 * `PrefixesPage` de aynı kalıbı kullanır; eşik tek kaynaktan ({@code CARD_VIEW_BREAKPOINT}).
 *
 * <p><b>Sayfalama istemci tarafındadır</b> (katalog tek istekte gelir): geniş ekranda paylaşılan
 * `ui/DataTable` kendi paginatörünü yönetir, dar ekranda kart listesinin altına derli toplu bir
 * paylaşılan `ui/CardPaginator` konur. Sunucu-taraflı (`lazy`) sayfalama <b>bilinçli olarak</b>
 * kullanılmadı: servis sırayı sabitliyor (ada göre) ve kolon başlıklarındaki sıralama istemcide
 * çalışıyor — lazy sayfalamada o başlıklar yalnız açık sayfayı sıralar, yani kullanıcıya yanlış söylerdi.
 *
 * <p><b>Arama ise sunucuda süzülür</b> (`q`): TR-farkında karşılaştırma (`i` ↔ `İ`) serviste yazılı,
 * istemcide ikinci bir kopyası tutulmaz. Adda (TR+EN) arar; Excel dışa aktarımı da aynı metni taşır.
 */
export default function MeetingTypesPage() {
  const { t } = useTranslation()
  const localized = useLocalizedText()
  // 126 — Kısıtlı Yetkili asistan ekranı SALT OKUNUR görür: Yeni, anahtar, düzenle ve sil hiç
  // çizilmez; durum rozetle okunmaya devam eder.
  const { readOnly } = useProxyMode()
  const [cardFirst, setCardFirst] = useState(0)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [search])

  // Kart sayfası başa YALNIZ arama fiilen değişince döner — gecikme zamanlayıcısının İÇİNDE
  // sıfırlamak KARARSIZLIK üretiyordu: etki mount'ta da kurulduğu için, arama hiç değişmese bile
  // açılıştan SEARCH_DEBOUNCE_MS sonra koşulsuz bir `setCardFirst(0)` ateşliyordu. Kullanıcı (ya da
  // test) o 300 ms dolmadan 2. sayfaya geçerse gecikmiş zamanlayıcı onu 1. sayfaya geri atıyordu;
  // hangisinin önce olacağı makine hızına bağlıydı. THY'nin Jenkins'i 25.09.2026'da tam bu yüzden
  // kırmızı döndü (HolidayDaysPage.test.tsx → "dar ekranda ... paginator vardir"; yerelde 3 koşudan
  // 1'i düşüyordu). Burada `debounced` değişmediğinde React aynı değere bail-out yapar, etki
  // yeniden çalışmaz — sıfırlama yalnız gerçek arama değişiminde olur.
  useEffect(() => {
    setCardFirst(0)
  }, [debounced])

  const { list, create, update, changeStatus, remove } = useMeetingTypes(debounced)
  const toast = useToast()

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<MeetingType | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)

  const items = list.data?.content ?? []
  const cardItems = items.slice(cardFirst, cardFirst + CARD_PAGE_SIZE)

  // Liste kısalınca (silme, arama) açık sayfa boşta kalabilir; DURUMUN KENDİSİ başa alınır — yalnız
  // render değerini kırpmak yetmez, bayat `cardFirst` sonraki eklemede kullanıcıyı yanlış sayfaya atar.
  useEffect(() => {
    setCardFirst((f) => (f >= items.length ? 0 : f))
  }, [items.length])

  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)
  const submitting = create.isPending || update.isPending
  const busyId = changeStatus.isPending
    ? (changeStatus.variables?.id ?? null)
    : remove.isPending
      ? (remove.variables ?? null)
      : null

  const notify = (severity: 'success' | 'error', summary: string) =>
    toast.show({ severity, summary, life: 3000 })

  const openCreate = () => {
    setEditing(null)
    setNameError(null)
    setFormOpen(true)
  }
  const openEdit = (row: MeetingType) => {
    setEditing(row)
    setNameError(null)
    setFormOpen(true)
  }

  // Domaine özel ele alma ekranda kalır; optimistic-lock/generic toast'ı merkez basar.
  const handleMutationError = (err: unknown) => {
    if (getApiErrorCode(err) === 'DUPLICATE_NAME') {
      setNameError(t('meetingTypes.errors.duplicate'))
      return
    }
    if (isOptimisticLock(err)) {
      setFormOpen(false)
      list.refetch()
    }
  }

  const onSubmit = (payload: MeetingTypeUpsert) => {
    if (editing) {
      update.mutate(
        { id: editing.id, body: { ...payload, modDate: editing.modDate } },
        {
          onSuccess: () => {
            notify('success', t('meetingTypes.toast.updated'))
            setFormOpen(false)
          },
          onError: handleMutationError,
        },
      )
      return
    }
    create.mutate(payload, {
      onSuccess: () => {
        notify('success', t('meetingTypes.toast.created'))
        setFormOpen(false)
      },
      onError: handleMutationError,
    })
  }

  const onToggle = (row: MeetingType, active: boolean) => {
    changeStatus.mutate(
      { id: row.id, body: { recordStatus: active ? 1 : 0, modDate: row.modDate } },
      { onSuccess: () => notify('success', t('meetingTypes.toast.statusChanged')) },
    )
  }

  const onDelete = (row: MeetingType) => {
    confirmDialog({
      header: t('meetingTypes.delete.title'),
      message: t('meetingTypes.delete.confirm', { name: row.nameTr }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('meetingTypes.delete.accept'),
      accept: () =>
        remove.mutate(row.id, {
          onSuccess: () => notify('success', t('meetingTypes.toast.deleted')),
        }),
    })
  }

  return (
    <div className="admin-page">
      <ConfirmDialog />
      <MeetingTypeFormDialog
        visible={formOpen}
        initial={editing}
        submitting={submitting}
        nameError={nameError}
        onClearNameError={() => setNameError(null)}
        onSubmit={onSubmit}
        onHide={() => setFormOpen(false)}
      />

      <PageHeader
        title={t('nav.admin.meetingTypes')}
        actions={
          <>
            <RefreshButton onRefresh={() => list.refetch()} />
            <ExcelExportButton onExport={() => exportMeetingTypes(debounced)} disabled={items.length === 0} />
            {!readOnly && (
              <Button icon="pi pi-plus" label={t('meetingTypes.actions.new')} onClick={openCreate} />
            )}
          </>
        }
      />

      <FilterBar
        searchValue={search}
        searchPlaceholder={t('meetingTypes.search')}
        onSearch={setSearch}
      />

      {isMobile ? (
        <div className="ui-card-list">
          {list.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
            ))}
          {!list.isLoading && items.length === 0 && (
            <EmptyState
              title={t('meetingTypes.empty.title')}
              description={t('meetingTypes.empty.desc')}
            />
          )}
          {!list.isLoading &&
            cardItems.map((row) => {
              const active = row.recordStatus === 1
              return (
                <div key={row.id} className="ui-card">
                  <div className="ui-card-top">
                    <Badge tone={active ? 'green' : 'neutral'}>
                      {active ? t('meetingTypes.status.active') : t('meetingTypes.status.passive')}
                    </Badge>
                    {!readOnly && (
                      <div className="ui-card-actions ui-card-actions--compact">
                        <InputSwitch
                          className="admin-switch"
                          checked={active}
                          disabled={busyId === row.id}
                          onChange={(e) => onToggle(row, Boolean(e.value))}
                          aria-label={row.nameTr}
                        />
                        <Button
                          icon="pi pi-pencil"
                          rounded
                          text
                          aria-label={t('meetingTypes.actions.editNamed', { name: row.nameTr })}
                          onClick={() => openEdit(row)}
                        />
                        <Button
                          icon="pi pi-trash"
                          rounded
                          text
                          severity="danger"
                          aria-label={t('meetingTypes.actions.deleteNamed', { name: row.nameTr })}
                          disabled={busyId === row.id}
                          onClick={() => onDelete(row)}
                        />
                      </div>
                    )}
                  </div>
                  <div className="ui-card-head">
                    <span className="ui-card-title">{localized(row.nameTr, row.nameEn)}</span>
                  </div>
                </div>
              )
            })}
          {!list.isLoading && items.length > CARD_PAGE_SIZE && (
            <CardPaginator
              first={cardFirst}
              rows={CARD_PAGE_SIZE}
              totalRecords={items.length}
              onPage={setCardFirst}
            />
          )}
        </div>
      ) : (
        <DataTable
          value={items}
          loading={list.isLoading}
          emptyMessage={
            <EmptyState
              title={t('meetingTypes.empty.title')}
              description={t('meetingTypes.empty.desc')}
            />
          }
        >
          {meetingTypeColumns({
            t,
            localized,
            busyId,
            onEdit: openEdit,
            onToggle,
            onDelete,
            readOnly,
          })}
        </DataTable>
      )}
    </div>
  )
}
